import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { Alert, Box, Button, Paper, Stack, TextField, Typography } from "@mui/material";
import authApi from "../api/authApi";

const getErrorMessage = (err, fallback) => {
  const data = err?.data || err?.response?.data;
  if (data?.message) return data.message;
  if (Array.isArray(data?.errors) && data.errors[0]?.msg) return data.errors[0].msg;
  return fallback;
};

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");
    setError("");
    setIsLoading(true);

    try {
      const data = await authApi.forgotPassword({ email });
      setMessage(data.message || "If the email exists, a reset link has been sent.");
    } catch (err) {
      setError(getErrorMessage(err, "Unable to send reset link right now."));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Reset Password | CaloriesPal</title>
        <meta name="description" content="Request a secure CaloriesPal password reset link." />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <Box
        sx={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          p: 2,
          background: "linear-gradient(135deg,#f8fafc,#dcfce7)",
        }}
      >
        <Paper
          component="form"
          onSubmit={handleSubmit}
          elevation={0}
          sx={{
            width: "min(520px, 100%)",
            p: { xs: 3, sm: 4 },
            borderRadius: 4,
            boxShadow: "0 24px 70px rgba(15,23,42,0.16)",
          }}
        >
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="h4" fontWeight={800}>
                Reset your password
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                Enter the email linked to your account and we will send a secure reset link.
              </Typography>
            </Box>
            {message && <Alert severity="success">{message}</Alert>}
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Account email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              fullWidth
              required
            />
            <Button type="submit" variant="contained" size="large" disabled={isLoading}>
              {isLoading ? "Sending..." : "Send reset link"}
            </Button>
            <Button onClick={() => navigate("/login")} sx={{ textTransform: "none" }}>
              Back to sign in
            </Button>
          </Stack>
        </Paper>
      </Box>
    </>
  );
};

export default ForgotPassword;
